import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { isChunkLoadError, RELOAD_KEY } from '@/utils/lazyWithRetry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);

    // Auto-reload once on chunk loading failures (stale deployment)
    if (isChunkLoadError(error) && !sessionStorage.getItem(RELOAD_KEY)) {
      sessionStorage.setItem(RELOAD_KEY, '1');
      window.location.reload();
      return;
    }

    Sentry.captureException(error, {
      contexts: {
        react: { componentStack: errorInfo.componentStack ?? undefined },
      },
    });

    this.setState({
      errorInfo: errorInfo.componentStack ?? undefined,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="flex h-screen items-center justify-center bg-[#F7F7F7]">
          <div className="max-w-md p-8 bg-white rounded-lg border border-[#E5E5E5] shadow-sm text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-red-50 rounded-full">
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-2">Something went wrong</h2>

            <p className="text-[#6B6B6B] mb-6">
              {this.state.error?.message ||
                'Failed to load this page. This might be due to a network issue or a problem with the application.'}
            </p>

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 bg-white border border-[#E5E5E5] text-[#1A1A1A] rounded-lg hover:bg-[#F7F7F7] transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                className="px-4 py-2 bg-[#A855F7] text-white rounded-lg hover:bg-[#9333EA] transition-colors"
              >
                Reload Page
              </button>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-[#6B6B6B] hover:text-[#1A1A1A]">
                  Error Details
                </summary>
                <pre className="mt-2 p-3 bg-[#F7F7F7] rounded text-xs overflow-auto max-h-40">
                  {this.state.error.stack}
                </pre>
                {this.state.errorInfo && (
                  <pre className="mt-2 p-3 bg-[#F7F7F7] rounded text-xs overflow-auto max-h-40">
                    {this.state.errorInfo}
                  </pre>
                )}
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
